import React, { useState, useEffect } from 'react';
import s from './SettingsPage.module.sass';
import UsersTab from './tabs/UsersTab';
import SapsTab from './tabs/SapsTab';
import MailTab from './tabs/MailTab';
import ModalStepsTab from './tabs/ModalStepsTab';
import AuditLogsTab from './tabs/AuditLogsTab';
import { getCurrentUserRole } from '../../utils/permissions';
import { loadConfigFlags } from '../../utils/configFlagCache';
import useToast from '../../hooks/useToast';
import Toast from '../Toast/Toast';
import toastStyles from '../Toast/Toast.module.sass';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

const readNovaMode = () => {
    try {
        const saved = localStorage.getItem('nova-mode');
        if (saved === 'light' || saved === 'dark') return saved;
    } catch (_) { /* приватный режим и т.п. */ }
    return 'dark';
};

const SettingsPage = () => {
    const [activeTab, setActiveTab] = useState('users');
    const [userRole, setUserRole] = useState(null);
    // Режим темы синхронизирован с основным приложением через общий
    // ключ localStorage 'nova-mode' — зависимость в обе стороны
    const [mode, setMode] = useState(readNovaMode);

    const toggleMode = (next) => {
        setMode(prev => {
            const value = next || (prev === 'dark' ? 'light' : 'dark');
            try { localStorage.setItem('nova-mode', value); } catch (_) { /* noop */ }
            return value;
        });
    };

    // Живая синхронизация, если тему переключили в другой вкладке/окне
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === 'nova-mode' && (e.newValue === 'light' || e.newValue === 'dark')) {
                setMode(e.newValue);
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    // /settings — отдельный React-корень без App, поэтому слушатели
    // app:toast и app:confirm монтируем здесь самостоятельно
    const { toasts, success, error, warning, info, hideToast } = useToast();
    useEffect(() => {
        const handler = (e) => {
            const { type, message, duration } = e.detail || {};
            switch (type) {
                case 'error': return error(message, duration ?? 3000);
                case 'warning': return warning(message, duration ?? 3000);
                case 'info': return info(message, duration ?? 3000);
                default: return success(message, duration ?? 3000);
            }
        };
        window.addEventListener('app:toast', handler);
        return () => window.removeEventListener('app:toast', handler);
    }, [success, error, warning, info]);

    useEffect(() => {
        getCurrentUserRole().then(role => {
            setUserRole(role);
        });
        // Прогреваем кэш флагов конфигов заранее: к моменту захода
        // в «Почту» или «Саппы» все флаги уже готовы и появляются сразу
        loadConfigFlags();
    }, []);

    useEffect(() => {
        const root = document.documentElement;
        const previousTheme = root.getAttribute('data-theme');
        const previousMode = root.getAttribute('data-nova-mode');

        root.setAttribute('data-theme', 'light');
        // Дублируем режим на documentElement, чтобы портированные в body
        // оверлеи (модалка редактирования) тоже получали светлые токены
        root.setAttribute('data-nova-mode', mode);

        return () => {
            if (previousTheme) root.setAttribute('data-theme', previousTheme);
            else root.removeAttribute('data-theme');
            if (previousMode) root.setAttribute('data-nova-mode', previousMode);
            else root.removeAttribute('data-nova-mode');
        };
    }, [mode]);

    const tabs = [
        { id: 'users', label: 'Юзеры' },
        { id: 'saps', label: 'Саппы' },
        { id: 'mail', label: 'Почта' },
        ...(userRole === 'admin' ? [{ id: 'logs', label: 'Логи' }] : []),
        { id: 'steps', label: 'Шаги', disabled: true }
    ];

    const renderTabContent = () => {
        switch (activeTab) {
            case 'users':
                return <UsersTab />;
            case 'saps':
                return <SapsTab />;
            case 'mail':
                return <MailTab />;
            case 'logs':
                return userRole === 'admin' ? <AuditLogsTab /> : <UsersTab />;
            case 'steps':
                return <ModalStepsTab />;
            default:
                return <UsersTab />;
        }
    };

    const renderTabIcon = (tabId) => {
        switch (tabId) {
            case 'users':
                return (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                );
            case 'saps':
                return (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H4a4 4 0 0 0-4 4v2" />
                        <circle cx="8" cy="7" r="4" />
                        <line x1="20" y1="8" x2="20" y2="14" />
                        <line x1="23" y1="11" x2="17" y2="11" />
                    </svg>
                );
            case 'mail':
                return (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2" />
                        <path d="M22 6v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6" />
                        <path d="m3 7 9 6 9-6" />
                    </svg>
                );
            case 'steps':
                return (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                );
            case 'logs':
                return (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                        <path d="M8 13h8" />
                        <path d="M8 17h5" />
                        <path d="M8 9h2" />
                    </svg>
                );
            default:
                return null;
        }
    };

    return (
        <div className={s.settingsPage} data-nova-mode={mode}>
            <ConfirmDialog />
            <div className={toastStyles.toastOverlay} data-ltr-force>
                {toasts.map(toast => (
                    <Toast
                        key={toast.id}
                        id={toast.id}
                        type={toast.type}
                        message={toast.message}
                        duration={toast.duration}
                        onClose={() => hideToast(toast.id)}
                    />
                ))}
            </div>
            <div className={s.container}>
                <header className={s.header}>
                    <div className={s.headerMain}>
                        <h1 className={s.title}>
                            <span className={s.titleIcon}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="3" />
                                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.1a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.1a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.1a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                                </svg>
                            </span>
                            <span>Настройки</span>
                        </h1>
                        <p className={s.subtitle}>Управление пользователями, доступами, письмами и сценариями</p>
                    </div>
                    <div className={s.headerActions}>
                        <div className={s.themeToggle} role="group" aria-label="Тема оформления">
                            <span className={`${s.themeThumb} ${mode === 'light' ? s.themeThumbLight : ''}`} aria-hidden="true" />
                            <button
                                type="button"
                                className={`${s.themeOption} ${mode === 'dark' ? s.themeOptionActive : ''}`}
                                onClick={() => toggleMode('dark')}
                                title="Тёмная тема"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                className={`${s.themeOption} ${mode === 'light' ? s.themeOptionActive : ''}`}
                                onClick={() => toggleMode('light')}
                                title="Светлая тема"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="4" />
                                    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
                                </svg>
                            </button>
                        </div>
                        {userRole === 'admin' && (
                            <button
                                className={s.backButton}
                                onClick={() => window.location.href = '/'}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                    <polyline points="9 22 9 12 15 12 15 22" />
                                </svg>
                                <span>Вернуться в приложение</span>
                            </button>
                        )}
                    </div>
                </header>

                <div className={s.tabsContainer}>
                    <div className={s.tabs}>
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                className={`${s.tab} ${activeTab === tab.id ? s.active : ''} ${tab.disabled ? s.tabDisabled : ''}`}
                                onClick={() => !tab.disabled && setActiveTab(tab.id)}
                                disabled={tab.disabled}
                            >
                                <span className={s.tabIcon}>{renderTabIcon(tab.id)}</span>
                                <span className={s.tabLabel}>{tab.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <section className={s.tabContent}>
                    {renderTabContent()}
                </section>
            </div>
        </div>
    );
};

export default SettingsPage;

